import { useState, useMemo } from 'react'
import { differenceInDays, parseISO } from 'date-fns'
import { useVehicles, useUpcomingVehicleReminders, useVehiclePhotoUrl } from './hooks/useVehicles'
import { useAuth } from '../../hooks/useAuth'
import { VehicleForm } from './components/VehicleForm'
import { VehicleDetail } from './components/VehicleDetail'
import { SkeletonList } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/ui/EmptyState'
import { cn } from '../../lib/utils'

const ReminderBadge = ({ count, hasOverdue, hasUrgent }) => {
  if (!count) return null
  return (
    <span className={cn(
      'text-xs font-semibold px-2 py-0.5 rounded-full',
      hasOverdue  ? 'bg-rose-100 text-rose-600' :
      hasUrgent   ? 'bg-amber-100 text-amber-700' :
                    'bg-primary-light text-primary'
    )}>
      {count} reminder{count > 1 ? 's' : ''}
    </span>
  )
}

const VehicleCard = ({ vehicle, upcomingReminders, onClick }) => {
  const { data: photoUrl } = useVehiclePhotoUrl(vehicle.photo_path)
  const vehicleReminders = upcomingReminders.filter((r) => r.vehicle_id === vehicle.id)

  const hasOverdue = vehicleReminders.some((r) =>
    r.due_date && differenceInDays(parseISO(r.due_date), new Date()) < 0
  )
  const hasUrgent = !hasOverdue && vehicleReminders.some((r) =>
    r.due_date && differenceInDays(parseISO(r.due_date), new Date()) <= 7
  )

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-surface rounded-xl border border-border overflow-hidden hover:border-primary/30 transition-colors"
    >
      {/* Photo or placeholder */}
      <div className="w-full h-36 bg-border/20 flex items-center justify-center overflow-hidden">
        {photoUrl ? (
          <img src={photoUrl} alt={`${vehicle.make} ${vehicle.model}`} className="w-full h-full object-cover" />
        ) : (
          <svg className="w-16 h-16 text-border" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <rect x="1" y="3" width="15" height="13" rx="2"/>
            <path d="m16 8 5 3v5h-5"/>
            <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
          </svg>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-sm text-text-primary">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </p>
            {vehicle.license_plate && (
              <p className="text-xs text-text-muted mt-0.5">{vehicle.license_plate}</p>
            )}
            {vehicle.profiles && (
              <p className="text-xs text-text-muted mt-0.5">{vehicle.profiles.display_name}</p>
            )}
          </div>
          <ReminderBadge
            count={vehicleReminders.length}
            hasOverdue={hasOverdue}
            hasUrgent={hasUrgent}
          />
        </div>
        {vehicle.current_mileage && (
          <p className="text-xs text-text-muted mt-1.5">{vehicle.current_mileage.toLocaleString()} mi</p>
        )}
      </div>
    </button>
  )
}

export const VehiclesPage = () => {
  const { isLimited } = useAuth()
  const { data: vehicles = [],          isLoading, create, update, remove } = useVehicles()
  const { data: upcomingReminders = [] }                                    = useUpcomingVehicleReminders()

  const [showForm, setShowForm]   = useState(false)
  const [editing,  setEditing]    = useState(null)
  const [viewing,  setViewing]    = useState(null)

  const handleEdit = (v) => {
    setViewing(null)
    setEditing(v)
    setShowForm(true)
  }

  const handleSave = async (payload) => {
    if (editing) {
      await update.mutateAsync({ id: editing.id, ...payload })
    } else {
      await create.mutateAsync(payload)
    }
  }

  if (isLoading) return <div className="px-4 py-5"><SkeletonList count={3} /></div>

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-text-primary">Vehicles</h1>
        {!isLimited && (
          <button
            onClick={() => { setEditing(null); setShowForm(true) }}
            className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Add
          </button>
        )}
      </div>

      {vehicles.length === 0 ? (
        <EmptyState
          type="vehicles"
          title="No vehicles yet"
          description="Track service records, fuel logs, and reminders for your household vehicles."
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {vehicles.map((v) => (
            <VehicleCard
              key={v.id}
              vehicle={v}
              upcomingReminders={upcomingReminders}
              onClick={() => setViewing(v)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <VehicleForm
          vehicle={editing}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditing(null) }}
        />
      )}
      {viewing && (
        <VehicleDetail
          vehicle={viewing}
          onEdit={handleEdit}
          onDelete={remove.mutateAsync}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  )
}
